import type { Aviso, Navegacion } from '../api/operaciones';
import type { NombreIcono } from '../componentes/iconos';
import { iconoDeFranja } from '../componentes/estiloFranja';
import type { Franja } from '../logica/franjas';
import { enlaceDeReferencia } from '../logica/navegacion';
import { haceCuanto } from '../logica/tiempo';

/**
 * Tarjetas de la campana (0.2): primero las franjas que no caben arriba (0.5, P0-9) y después los avisos de Entregas
 * (`listMyNotices`). Toda la tarjeta toma su color: rojo (crítico), amarillo (pendiente) o gris (información). El enlace
 * de un aviso (`link`, tipo e id) sale de los destinos de `getMyNavigation` (`link_targets`, G298) con
 * `enlaceDeReferencia`; si la persona no puede ver ese destino, la tarjeta no lleva enlace. El título ya llega en el texto
 * del aviso (p. ej. `*.cobro_cargo_fallido` dice si fue un «no vino» o una cancelación tardía, G809): la campana no mira el tipo.
 */
export type Tono = 'rojo' | 'aviso' | 'info';

export interface ElementoCampana {
    clave: string;
    tono: Tono;
    icono: NombreIcono;
    titulo: string;
    texto: string;
    hora: string;
    accion: string | null;
    /** Adónde lleva la acción de un aviso (su `link` con `link_targets`); null en las franjas o sin enlace. */
    ruta: string | null;
    franja: Franja | null;
    aviso: Aviso | null;
}

export const COLORES_TONO: Record<Tono, { fondo: string; tinta: string; sub: string; baldosa: string }> = {
    rojo: { fondo: 'var(--rv-red)', tinta: '#fff', sub: 'rgba(255,255,255,0.82)', baldosa: 'rgba(255,255,255,0.22)' },
    aviso: { fondo: 'var(--rv-warning)', tinta: 'var(--rv-ink)', sub: 'rgba(17,17,17,0.72)', baldosa: 'rgba(255,255,255,0.22)' },
    info: { fondo: 'var(--rv-paper)', tinta: 'var(--rv-ink)', sub: 'var(--rv-fg-2)', baldosa: '#fff' },
};

const TONO_FRANJA: Record<Franja['tipo'], Tono> = {
    suplantacion: 'rojo',
    reservaria: 'info',
    'solo-lectura': 'rojo',
    impago: 'rojo',
    soporte: 'info',
    cupo100: 'rojo',
    prueba: 'aviso',
    baja: 'aviso',
    cupo: 'aviso',
};

const TONO_GRAVEDAD: Record<Aviso['severity'], Tono> = { critical: 'rojo', warning: 'aviso', info: 'info' };
const ICONO_GRAVEDAD: Record<Aviso['severity'], NombreIcono> = { critical: 'triangle-alert', warning: 'circle-alert', info: 'info' };

/**
 * Icono propio de un tipo de aviso (`notice_type`), si lo tiene; el resto usa el de su gravedad. `plataforma.acceso_abierto`
 * (C.61) lleva `headset` en solo lectura y `user-cog` en suplantación (`params.modo`, el mismo icono que la franja).
 */
const ICONO_POR_TIPO: Record<string, (a: Aviso) => NombreIcono> = {
    'plataforma.acceso_abierto': (a) => (a.params?.['modo'] === 'suplantacion' ? 'user-cog' : 'headset'),
};

export function iconoDeAviso(a: Aviso): NombreIcono {
    return ICONO_POR_TIPO[a.notice_type]?.(a) ?? ICONO_GRAVEDAD[a.severity];
}

/**
 * «hace 2 h · 4 veces · Crítico · también por email»: cuándo, si se repite y si también llegó por email (`emailed`).
 */
export function lineaDeHora(a: Aviso, ahora = new Date()): string {
    const partes = [haceCuanto(a.last_occurred_at ?? a.created_at, ahora)];
    if (a.occurrences > 1) {
        partes.push(`${String(a.occurrences)} veces`);
    }
    if (a.severity === 'critical') {
        partes.push(a.emailed === true ? 'Crítico · también por email' : 'Crítico');
    } else if (a.emailed === true) {
        partes.push('También por email');
    }
    return partes.join(' · ');
}

/**
 * Lo que dice la página de avisos vacía (5.1). Si a la persona no le llega ningún tipo (`listMyNoticeTypes` vacío: el Socio
 * de salida, G43), lo dice; el panel no mira el rol (no tiene tablas por rol).
 */
export function textoSinAvisos(sinTipos: boolean): string {
    return sinTipos ? 'No recibes avisos. Solo te llegan los que el negocio te asigne en «Quién recibe avisos».' : 'No tienes avisos';
}

export function elementosCampana(franjas: readonly Franja[], avisos: readonly Aviso[] | null, destinos: Navegacion['link_targets'] = [], ahora = new Date()): ElementoCampana[] {
    return [
        ...franjas.map((f): ElementoCampana => ({
            clave: `franja-${f.tipo}`,
            tono: TONO_FRANJA[f.tipo],
            icono: f.tipo === 'suplantacion' ? 'user-cog' : iconoDeFranja(f.tipo),
            titulo: f.titulo,
            texto: f.texto,
            hora: 'ahora',
            accion: f.accion,
            ruta: null,
            franja: f,
            aviso: null,
        })),
        ...(avisos ?? []).map((a): ElementoCampana => {
            const enlace = a.link == null ? null : enlaceDeReferencia(destinos, a.link.type, a.link.id, a.link_label);
            return {
                clave: a.id,
                tono: TONO_GRAVEDAD[a.severity],
                icono: iconoDeAviso(a),
                titulo: a.title ?? '',
                texto: a.body ?? '',
                hora: lineaDeHora(a, ahora),
                accion: enlace?.etiqueta ?? null,
                ruta: enlace?.ruta ?? null,
                franja: null,
                aviso: a,
            };
        }),
    ];
}
